import { FC, useMemo } from 'react';
import { Modal, Typography } from '@crisellevaldez/speculo-ui';
import { useConversationThreadsGQL } from '../../../hooks/graphql/dashboard/useConversationThreadsGQL';
import { useOrgSmsMessagesGQL } from '../../../hooks/graphql/dashboard/useOrgSmsMessagesGQL';
import { useContactSmsAroundGQL } from '../../../hooks/graphql/contacts/useContactSmsAroundGQL';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { formatPhoneNumber } from '../../../utils/formatters';
import { SmsChatList, type SmsChatMessage } from '../contacts/activity/SmsChatList';

/**
 * Anchor data passed in by the caller when opening the thread at a
 * specific message (activity-feed row click). The anchor row itself is
 * NOT returned by backend's `before`/`after` cursors (per PR #2423 —
 * anchor is exclusive), so the caller passes the row data it already
 * has and the modal renders the bubble locally between the older and
 * newer buckets.
 */
export interface SmsThreadAnchor {
  messageId: string;
  body?: string | null;
  direction?: string | null;
  sentAt?: string | null;
  createdAt?: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  contactId: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  /**
   * Contact's total SMS count for the subtitle — caller (SmsStatsModal)
   * has `messageCount` directly from the conversationThreads row and
   * passes it in so the modal shows "107 messages" not "5 messages
   * currently loaded". Optional: activity-feed callers don't have it,
   * subtitle falls back to messages.length there.
   */
  totalCount?: number | null;
  /**
   * Specific conversation to drill into. Passed from the SMS dashboard
   * modal where the row already carries the thread id. Omit (or pass
   * null) when the caller only has a contactId (Recent Activities) —
   * the modal will resolve the contact's most-recent thread.
   */
  conversationId?: string | null;
  /**
   * Anchored open — fetches messages around the anchor (PR #2423
   * cursor params), unions them with the anchor itself, and scrolls
   * the chat to the anchor bubble. Pass only when the caller has the
   * full row data (activity-feed row click). Omit for plain
   * thread view backed by `useOrgSmsMessagesGQL`.
   */
  anchor?: SmsThreadAnchor | null;
}

/**
 * Two render modes, both delegated to the shared SmsChatList renderer:
 *
 * 1. **Anchored** (caller passes `anchor`) — activity-feed flows. The
 *    around-query loads N strictly older + N strictly newer; we union
 *    with the synthetic anchor row and pass to SmsChatList which scrolls
 *    to it.
 *
 * 2. **Plain thread** (no anchor, just contactId + optional
 *    conversationId) — dashboard SMS modal row click. Resolves the
 *    contact's latest conversation if no conversationId provided, then
 *    loads its messages.
 */
export const SmsThreadModal: FC<Props> = ({
  isOpen,
  onClose,
  contactId,
  contactName,
  contactPhone,
  conversationId,
  anchor,
  totalCount,
}) => {
  const timezone = useOrganizationStore((s) => s.organization?.timezone) ?? 'UTC';
  const assistantName = useOrganizationStore((s) => s.organization?.assistant_name) || 'Remi';
  const contactFirstName = useMemo(() => {
    if (!contactName || contactName === 'unknown') return undefined;
    return contactName.trim().split(/\s+/)[0];
  }, [contactName]);

  const anchoredMode = isOpen && !!anchor?.messageId && !!contactId;

  // ── Anchored mode ──────────────────────────────────────────────
  // Start with a tight window — 3 older + anchor + 3 newer — so the
  // user lands focused on the clicked message with just a sliver of
  // surrounding context. Further history loads automatically as the
  // user scrolls toward either edge (SmsChatList IntersectionObserver
  // → useContactSmsAroundGQL.loadOlder/loadNewer).
  const around = useContactSmsAroundGQL({
    contactId,
    anchorMessageId: anchor?.messageId,
    enabled: anchoredMode,
    limit: 3,
  });

  // ── Plain-thread mode ──────────────────────────────────────────
  const needsResolve = isOpen && !anchoredMode && !conversationId && !!contactId;
  const threads = useConversationThreadsGQL({
    channel: 'sms',
    contactId: contactId ?? undefined,
    enabled: needsResolve,
  });
  const resolvedConversationId =
    conversationId ?? threads.items[0]?.conversationId ?? undefined;

  const plain = useOrgSmsMessagesGQL({
    dateRange: { from: null, to: null },
    conversationId: resolvedConversationId,
    enabled: isOpen && !anchoredMode && !!resolvedConversationId,
    sortOrder: 'ASC',
  });

  // ── Map to SmsChatList shape (ASC ordered) ────────────────────
  // Hold the messages array until the anchored query resolves —
  // otherwise the list renders briefly as `[anchor]` alone, the scroll
  // effect centres on it, and then 25 older bubbles prepend above and
  // push the anchor off-screen at the bottom. By the time the second
  // batch lands, the scroll-guard says "already scrolled" and we never
  // re-fire. Waiting for `around.loading=false` makes the length jump
  // 0 → ~26 in one go so SmsChatList scrolls once at the right moment.
  const messages: SmsChatMessage[] = useMemo(() => {
    const normaliseDirection = (d: string | null | undefined): 'inbound' | 'outbound' =>
      (d || '').toLowerCase() === 'inbound' ? 'inbound' : 'outbound';

    if (anchoredMode && anchor) {
      if (around.loading) return [];
      const olderMsgs: SmsChatMessage[] = around.older.map((m) => ({
        smsId: m.messageId,
        body: m.body || '',
        direction: normaliseDirection(m.direction),
        fromNumber: m.fromAddress ?? '',
        sentAt: m.sentAt || m.createdAt || '',
        deliveredAt: m.deliveredAt,
        readAt: m.openedAt,
      }));
      const newerMsgs: SmsChatMessage[] = around.newer.map((m) => ({
        smsId: m.messageId,
        body: m.body || '',
        direction: normaliseDirection(m.direction),
        fromNumber: m.fromAddress ?? '',
        sentAt: m.sentAt || m.createdAt || '',
        deliveredAt: m.deliveredAt,
        readAt: m.openedAt,
      }));
      const anchorMsg: SmsChatMessage = {
        smsId: anchor.messageId,
        body: anchor.body || '',
        direction: normaliseDirection(anchor.direction),
        sentAt: anchor.sentAt || anchor.createdAt || '',
      };
      return [...olderMsgs, anchorMsg, ...newerMsgs];
    }

    return plain.items.map((m) => ({
      smsId: m.messageId,
      body: m.body || '',
      direction: normaliseDirection(m.direction),
      sentAt: m.sentAt || m.createdAt || '',
    }));
  }, [anchoredMode, anchor, around.loading, around.older, around.newer, plain.items]);

  const headerName = contactName && contactName !== 'unknown' ? contactName : null;
  const headerPhone = contactPhone ? formatPhoneNumber(contactPhone) : null;
  // Prefer the caller-supplied total (SmsStatsModal hands us the
  // contact's full SMS count from the row) so the subtitle matches
  // SmsTab's behaviour. Fall back to plain.total in conversation-id
  // mode, or messages.length when neither is available.
  const resolvedTotal =
    typeof totalCount === 'number' && totalCount > 0
      ? totalCount
      : anchoredMode
        ? messages.length
        : plain.total;
  const subtitle = [headerPhone, resolvedTotal ? `${resolvedTotal} messages` : null]
    .filter(Boolean)
    .join(' · ');

  const loading = anchoredMode
    ? around.loading && messages.length === 0
    : (threads.isLoading || plain.isLoading) && messages.length === 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <div className="flex flex-col">
          <Typography variant="h3">{headerName ?? 'SMS thread'}</Typography>
          {subtitle && (
            <span className="text-xs text-muted-foreground mt-0.5">{subtitle}</span>
          )}
        </div>
      </Modal.Header>
      <Modal.Body className="flex-1 flex flex-col min-h-0 p-0">
        {loading ? (
          // Skeleton matches the chat-list layout (alternating bubble
          // shapes + day divider) so the swap-in feels like content
          // filling in, not a layout shift. Same visual language as
          // the CallDetailsModal skeleton — both modals now read the
          // same way while data is fetching.
          <div className="flex flex-col gap-3 p-4 animate-pulse" aria-hidden>
            <div className="flex justify-center">
              <div className="h-3 w-20 rounded-full bg-foreground/10" />
            </div>
            <div className="flex justify-start">
              <div className="h-10 w-2/3 rounded-2xl bg-foreground/10" />
            </div>
            <div className="flex justify-end">
              <div className="h-10 w-1/2 rounded-2xl bg-foreground/10" />
            </div>
            <div className="flex justify-start">
              <div className="h-8 w-1/2 rounded-2xl bg-foreground/10" />
            </div>
            <div className="flex justify-end">
              <div className="h-12 w-3/5 rounded-2xl bg-foreground/10" />
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex justify-center py-8 text-sm text-muted-foreground">
            No messages in this thread.
          </div>
        ) : (
          <SmsChatList
            messages={messages}
            timezone={timezone}
            contactFirstName={contactFirstName}
            assistantName={assistantName}
            scrollToMessageId={anchor?.messageId ?? null}
            // Anchored mode lands at the anchor — don't also jump to
            // latest. Plain mode opens at the bottom (latest message).
            autoScrollToLatestOnLoad={!anchoredMode}
            // Anchored mode opens with a tight window (3 + anchor + 3);
            // both directions auto-load via SmsChatList's scroll-edge
            // sentinels, wired to the cursor pagination from PR #2423.
            hasOlder={anchoredMode ? around.hasOlder : false}
            onLoadOlder={anchoredMode ? around.loadOlder : undefined}
            isLoadingOlder={anchoredMode ? around.isLoadingOlder : false}
            hasNewer={anchoredMode ? around.hasNewer : false}
            onLoadNewer={anchoredMode ? around.loadNewer : undefined}
            isLoadingNewer={anchoredMode ? around.isLoadingNewer : false}
            resetKey={`${contactId ?? ''}|${anchor?.messageId ?? ''}|${resolvedConversationId ?? ''}`}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SmsThreadModal;
